// An admin viewing as a user, per tab (ADR 0017's A19 amendment): the user's pages live
// under /view-as/$userId, whose requests carry X-View-As. Links and navigation keep the
// prefix while the current page has it, so the viewed user's views needn't know.
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';

const PREFIX = /^\/view-as\/([^/]+)/;

/** The viewed user's ID in a path under /view-as/, or null. */
export function viewAsOf(pathname: string): string | null {
  return PREFIX.exec(pathname)?.[1] ?? null;
}

/** The path without its /view-as/$userId prefix: the page it shows. */
export function withoutViewAs(pathname: string): string {
  return pathname.replace(PREFIX, '') || '/';
}

/** An in-app path (with any query) under /view-as/$userId, or unchanged without one. */
export function withViewAs(path: string, userId: string | null): string {
  if (!userId || !path.startsWith('/') || viewAsOf(path)) return path;
  return `/view-as/${userId}${path === '/' ? '' : path}`;
}

/** The viewed user's ID while this tab views as someone, else null. */
export function useViewAs(): string | null {
  return viewAsOf(useLocation({ select: (location) => location.pathname }));
}

/** `path`, under the current /view-as/ prefix when there is one. */
export function useViewAsHref(path: string): string {
  return withViewAs(path, useViewAs());
}

/**
 * Navigates to an in-app path (MM-DD-YYYY dates already in it), keeping the /view-as/
 * prefix: for the views the admin and the viewed user share.
 */
export function useGo(): (path: string) => void {
  const navigate = useNavigate();
  const viewAs = useViewAs();
  return useCallback(
    (path: string) => void navigate({ href: withViewAs(path, viewAs) }),
    [navigate, viewAs],
  );
}
